import * as React from "react";
import { Component } from "react";
import { Alert } from "@mui/material";
import TextField from "Components/Input/TextField";
import Button from "Components/Buttons/Button";

// Must match the client_id used in the OAuth login flow
const MICROSOFT_CLIENT_ID = "b8f5a2b8-79d6-4d05-a766-3cd6141cfbe3";

interface AdminConsentPanelState {
    tenantId: string;
    consentResult: "success" | "error" | "cancelled" | null;
    resultTenant?: string;
    errorDetail?: string;
    isWaiting: boolean;
}

export default class AdminConsentPanel extends Component<Record<string, never>, AdminConsentPanelState> {
    constructor(props: Record<string, never>) {
        super(props);
        this.state = {
            tenantId: "",
            consentResult: null,
            isWaiting: false
        };
    }

    private getRedirectUri(): string {
        return location.origin === "file://" ? "http://localhost:5000/" : location.origin;
    }

    private buildConsentUrl(): string {
        const tenant = this.state.tenantId.trim() || "organizations";
        const params = new URLSearchParams({
            client_id: MICROSOFT_CLIENT_ID,
            redirect_uri: this.getRedirectUri()
        });
        return `https://login.microsoftonline.com/${encodeURIComponent(tenant)}/adminconsent?${params.toString()}`;
    }

    private openConsent() {
        const consentUrl = this.buildConsentUrl();
        const adminConsentApi = (window as any).adminConsentApi;

        if (adminConsentApi) {
            // Desktop: open in popup and detect the result when Microsoft redirects back
            this.setState({ isWaiting: true, consentResult: null });

            adminConsentApi.onResult((result: {
                adminConsent: string | null;
                tenant: string | null;
                error: string | null;
                errorDescription: string | null;
            }) => {
                adminConsentApi.removeResultListener();

                if (result.adminConsent === "True") {
                    this.setState({ isWaiting: false, consentResult: "success", resultTenant: result.tenant || undefined });
                } else if (result.error === "cancelled") {
                    this.setState({ isWaiting: false, consentResult: "cancelled" });
                } else {
                    this.setState({
                        isWaiting: false,
                        consentResult: "error",
                        errorDetail: result.errorDescription || result.error || "Unknown error"
                    });
                }
            });

            adminConsentApi.open(consentUrl);
        } else {
            // Web: open in a new tab — Microsoft will show its own confirmation page
            window.open(consentUrl, "_blank", "noopener,noreferrer");
        }
    }

    render() {
        const { consentResult, resultTenant, errorDetail, isWaiting } = this.state;
        const adminConsentApi = (window as any).adminConsentApi;

        return (
            <div style={{ maxWidth: 500 }}>
                <h3 style={{ marginTop: 0, marginBottom: 8 }}>Microsoft SSO Setup</h3>
                <p style={{ color: "#555", fontSize: "0.9em", marginBottom: 16 }}>
                    To allow users in a customer organisation to sign in with their Microsoft account,
                    their Azure AD administrator must grant consent for iDART once.
                    This is a one-time step per organisation.
                </p>

                <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", marginBottom: 4, fontSize: "0.9em", fontWeight: "bold" }}>
                        Customer Tenant ID{" "}
                        <span style={{ fontWeight: "normal", color: "#666" }}>(optional)</span>
                    </label>
                    <TextField
                        width={340}
                        label="e.g. contoso.onmicrosoft.com or tenant GUID"
                        initialValue=""
                        isDisabled={isWaiting}
                        onValueChanged={change => this.setState({ tenantId: change.newValue || "", consentResult: null })}
                    />
                    <p style={{ fontSize: "0.8em", color: "#666", marginTop: 4 }}>
                        This is the customer&apos;s Azure Entra (Azure AD) Tenant ID — found in the Azure Portal under
                        Microsoft Entra ID &gt; Overview. It can be entered as a domain (e.g. contoso.onmicrosoft.com)
                        or as the Tenant GUID shown on that page. Leave blank to let Microsoft prompt the admin to
                        choose their organisation when the consent page opens.
                    </p>
                </div>

                {consentResult === "success" && (
                    <Alert severity="success" style={{ marginBottom: 12 }}>
                        Admin consent granted successfully.
                        {resultTenant ? ` Tenant ID: ${resultTenant}` : ""}
                    </Alert>
                )}
                {consentResult === "error" && (
                    <Alert severity="error" style={{ marginBottom: 12 }}>
                        Consent was not granted. {errorDetail}
                    </Alert>
                )}
                {consentResult === "cancelled" && (
                    <Alert severity="warning" style={{ marginBottom: 12 }}>
                        Consent window was closed without completing.
                    </Alert>
                )}

                <Button
                    label={isWaiting ? "Waiting for consent..." : "Grant Admin Consent"}
                    onClick={() => this.openConsent()}
                    isDisabled={isWaiting}
                />

                {!adminConsentApi && (
                    <p style={{ fontSize: "0.8em", color: "#666", marginTop: 8 }}>
                        A new browser tab will open. The admin completes consent on Microsoft&apos;s page,
                        then can return to iDART.
                    </p>
                )}
            </div>
        );
    }
}
